import { Ban, CheckCircle2, Clock3, Copy, Gift, Link as LinkIcon, UsersRound } from "lucide-react";
import { useMemo, useState } from "react";
import { Badge, formatDate } from "../components/Badge";
import { Empty, ErrorState, Loading, Success } from "../components/State";
import { Input } from "../components/Form";
import { api } from "../lib/api";
import { useAsync } from "../lib/useAsync";

type Currency = "CNY" | "USD" | string;
type InviteRecord = {
  id: string;
  user_name?: string;
  masked_phone?: string;
  registered_at?: string;
  first_recharged_at?: string;
  reward_status?: "pending" | "settled" | "revoked" | string;
  reward_amount_cents?: number;
  reward_amount?: number;
  currency?: Currency;
};
type InviteSummary = {
  code?: string;
  invite_url?: string;
  share_url?: string;
  direct_invitees?: number;
  paying_users?: number;
  pending_amount_cents?: number;
  settled_amount_cents?: number;
  revoked_amount_cents?: number;
  pending_amount?: number;
  settled_amount?: number;
  revoked_amount?: number;
  reward_rate?: number;
  currency?: Currency;
  items?: InviteRecord[];
  invitees?: InviteRecord[];
};

export function InvitePage() {
  const result = useAsync(() => api<InviteSummary>("/invite"), []);
  const [message, setMessage] = useState("");
  const currency = result.data?.currency || "CNY";
  const inviteUrl = useMemo(() => {
    const supplied = result.data?.share_url || result.data?.invite_url;
    if (supplied) {
      try {
        return new URL(supplied, window.location.origin).toString();
      } catch {
        // Fall through to the local route when a provider returns an invalid URL.
      }
    }
    return result.data?.code ? `${window.location.origin}/login?inviter=${result.data.code}` : "";
  }, [result.data?.code, result.data?.invite_url, result.data?.share_url]);
  const records = result.data?.items || result.data?.invitees || [];

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const input = document.createElement("textarea");
      input.value = value;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      input.remove();
    }
    setMessage("邀请链接已复制");
  };

  if (result.loading) return <Loading label="正在读取邀请数据" />;
  if (result.error || !result.data) return <ErrorState error={result.error || new Error("邀请数据不存在")} retry={result.retry} />;

  return <div className="account-page page-shell invite-page">
    <header className="page-heading split"><div><span>INVITE REWARDS</span><h1>邀请奖励</h1><p>所有注册人都是平台用户；奖励只按直接邀请关系计算，被邀请人购买平台余额后返利到账。</p></div><button className="secondary-button" onClick={() => void result.retry()}><UsersRound size={16} />刷新统计</button></header>
    {message && <Success>{message}</Success>}

    <section className="metric-grid platform-metrics invite-metrics">
      <article className="accent"><span><UsersRound size={20} /></span><div><small>直接邀请用户</small><strong>{(result.data.direct_invitees ?? records.length).toLocaleString("zh-CN")}</strong></div></article>
      <article><span><Gift size={20} /></span><div><small>已充值用户</small><strong>{(result.data.paying_users ?? records.filter((item) => item.first_recharged_at).length).toLocaleString("zh-CN")}</strong></div></article>
      <article><span><Clock3 size={20} /></span><div><small>待确认奖励</small><strong>{formatMoney(readAmount(result.data, "pending"), currency)}</strong></div></article>
      <article><span><CheckCircle2 size={20} /></span><div><small>已到账奖励</small><strong>{formatMoney(readAmount(result.data, "settled"), currency)}</strong></div></article>
    </section>

    <div className="invite-overview-grid">
      <section className="admin-panel platform-panel invite-share-panel">
        <header><div><span>SHARE LINK</span><h2>我的邀请链接</h2><p>邀请注册只绑定用户关系，不绑定具体产品；被邀请人需要为自己的企业创建并使用应用。</p></div></header>
        <div className="copy-row"><Input readOnly value={inviteUrl} placeholder="邀请链接生成中" /><button className="icon-button" disabled={!inviteUrl} title="复制邀请链接" onClick={() => void copy(inviteUrl)}><Copy size={16} /></button></div>
        <div className="platform-rule-list invite-rule-list">
          <article><span className="rule-icon"><Gift size={17} /></span><div><strong>充值后获得奖励</strong><p>被邀请人成功购买平台余额并到账后，奖励自动计入你的平台余额。</p></div><Badge tone="accent">{formatRate(result.data.reward_rate)}</Badge></article>
          <article><span className="rule-icon"><UsersRound size={17} /></span><div><strong>只计算直接邀请</strong><p>邀请关系不绑定产品，不设置一级、二级或多级分销链路。</p></div><Badge>直接关系</Badge></article>
          <article><span className="rule-icon"><Ban size={17} /></span><div><strong>奖励仅供平台消费</strong><p>奖励余额不可提现、转账或交易，可用于任意平台 AI 应用。</p></div><Badge tone="success">仅消费</Badge></article>
        </div>
      </section>

      <section className="admin-panel platform-panel invite-status-panel">
        <header><div><span>REWARD STATUS</span><h2>奖励状态</h2><p>每笔奖励都保留确认、入账和撤销记录。</p></div></header>
        <div className="invite-status-list">
          <article><span className="pending"><Clock3 size={16} /></span><div><strong>待确认</strong><small>等待支付确认</small></div><b>{formatMoney(readAmount(result.data, "pending"), currency)}</b></article>
          <article><span className="settled"><CheckCircle2 size={16} /></span><div><strong>已到账</strong><small>已计入平台余额</small></div><b>{formatMoney(readAmount(result.data, "settled"), currency)}</b></article>
          <article><span className="revoked"><Ban size={16} /></span><div><strong>已撤销</strong><small>风控复核后撤销</small></div><b>{formatMoney(readAmount(result.data, "revoked"), currency)}</b></article>
        </div>
        <p className="feedback success"><LinkIcon size={17} />邀请关系以注册时绑定为准，后续使用多个产品仍归属同一平台用户关系。</p>
      </section>
    </div>

    <section className="detail-section account-detail-section invite-records-section">
      <div className="account-section-heading"><span>DIRECT INVITEES</span><h2>直接邀请明细</h2><p>这里只展示由你的邀请链接注册的用户，不展示多级链路。</p></div>
      {!records.length ? <Empty title="还没有直接邀请用户" detail="复制你的邀请链接发送给客户或合作伙伴。" /> : <div className="report-list">{records.map((item) => <article className={`reward-record ${rewardTone(item.reward_status)}`} key={item.id}><span>{statusIcon(item.reward_status)}</span><div><div>{rewardBadge(item.reward_status)}<small>{item.masked_phone || "平台用户"}</small></div><strong>{item.user_name || "未设置昵称用户"}</strong><p>注册 {formatDate(item.registered_at)}{item.first_recharged_at ? ` · 首次充值 ${formatDate(item.first_recharged_at)}` : " · 尚未充值"}</p></div><strong>{formatMoney(readRecordAmount(item), item.currency || currency)}</strong></article>)}</div>}
    </section>
  </div>;
}

function readAmount(value: InviteSummary, key: "pending" | "settled" | "revoked") {
  const cents = value[`${key}_amount_cents` as keyof InviteSummary];
  if (typeof cents === "number") return cents;
  const amount = value[`${key}_amount` as keyof InviteSummary];
  return typeof amount === "number" ? Math.round(amount * 100) : 0;
}

function readRecordAmount(value: InviteRecord) {
  if (typeof value.reward_amount_cents === "number") return value.reward_amount_cents;
  return typeof value.reward_amount === "number" ? Math.round(value.reward_amount * 100) : 0;
}

function formatMoney(cents: number, currency: Currency) {
  return new Intl.NumberFormat(currency === "USD" ? "en-US" : "zh-CN", { style: "currency", currency, minimumFractionDigits: 2 }).format(cents / 100);
}

function formatRate(value?: number) {
  if (typeof value !== "number") return "按规则";
  return `${value > 1 ? value : value * 100}%`;
}

function rewardBadge(status?: string) {
  const tone = status === "settled" ? "success" : status === "pending" ? "warning" : status === "revoked" ? "danger" : "neutral";
  return <Badge tone={tone}>{({ pending: "待确认", settled: "已到账", revoked: "已撤销" } as Record<string, string>)[status || ""] || status || "未触发"}</Badge>;
}

function rewardTone(status?: string) {
  return status === "settled" ? "settled" : status === "revoked" ? "revoked" : "pending";
}

function statusIcon(status?: string) {
  if (status === "settled") return <CheckCircle2 size={18} />;
  if (status === "revoked") return <Ban size={18} />;
  return <Clock3 size={18} />;
}
